import { motion } from 'framer-motion'
import { Gift, ShoppingCart, TrendingDown, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

interface CreditPack {
  source: string
  label: string
  initial: number
  remaining: number
  used: number
  expires_at: string
  created_at: string
  is_expired: boolean
  consumes_next: boolean
}

interface LedgerEvent {
  event_type: string
  description: string
  credits: number
  date: string
  detail: string | null
}

interface CreditLedgerData {
  total_balance: number
  credit_packs: CreditPack[]
  history: LedgerEvent[]
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function daysUntil(iso: string) {
  const diff = new Date(iso).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

export function CreditLedger({ data }: { data: CreditLedgerData }) {
  const activePacks = data.credit_packs.filter((p) => !p.is_expired && p.remaining > 0)
  const expiredPacks = data.credit_packs.filter((p) => p.is_expired || p.remaining === 0)

  return (
    <div className="space-y-6">
      {/* Active Credit Packs */}
      {activePacks.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Active Credit Packs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {activePacks.map((pack, i) => (
                <div
                  key={i}
                  className={cn(
                    'p-4 rounded-lg border transition-colors',
                    pack.consumes_next
                      ? 'border-primary/30 bg-primary/5'
                      : 'border-border bg-muted/20',
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {pack.source === 'promo' ? (
                        <Gift className="w-4 h-4 text-success" />
                      ) : (
                        <ShoppingCart className="w-4 h-4 text-primary" />
                      )}
                      <span className="font-medium text-sm">{pack.label}</span>
                      {pack.consumes_next && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 border-primary/40 text-primary"
                        >
                          Using now
                        </Badge>
                      )}
                    </div>
                    <span className="text-sm font-semibold">
                      {pack.remaining}{' '}
                      <span className="text-muted-foreground font-normal">/ {pack.initial}</span>
                    </span>
                  </div>
                  <Progress value={(pack.remaining / pack.initial) * 100} className="h-1.5 mb-2" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{pack.used} used</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {daysUntil(pack.expires_at)} days left
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* History Timeline */}
      {data.history.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Credit History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                {data.history.map((event, i) => (
                  <div key={i} className="flex items-center gap-3 py-2.5 border-b last:border-0">
                    <div
                      className={cn(
                        'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
                        event.event_type === 'redemption' && 'bg-success/10',
                        event.event_type === 'purchase' && 'bg-primary/10',
                        event.event_type === 'usage' && 'bg-muted',
                      )}
                    >
                      {event.event_type === 'redemption' && (
                        <Gift className="w-3.5 h-3.5 text-success" />
                      )}
                      {event.event_type === 'purchase' && (
                        <ShoppingCart className="w-3.5 h-3.5 text-primary" />
                      )}
                      {event.event_type === 'usage' && (
                        <TrendingDown className="w-3.5 h-3.5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{event.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.event_type === 'usage' && event.detail
                          ? event.detail
                          : formatDate(event.date)}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'text-sm font-semibold tabular-nums flex-shrink-0',
                        event.credits > 0 ? 'text-success' : 'text-muted-foreground',
                      )}
                    >
                      {event.credits > 0 ? '+' : ''}
                      {event.credits}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Expired / Depleted Packs */}
      {expiredPacks.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-muted-foreground">Past Credit Packs</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {expiredPacks.map((pack, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-2 text-sm text-muted-foreground"
                >
                  <div className="flex items-center gap-2">
                    {pack.source === 'promo' ? (
                      <Gift className="w-3.5 h-3.5" />
                    ) : (
                      <ShoppingCart className="w-3.5 h-3.5" />
                    )}
                    <span>{pack.label}</span>
                  </div>
                  <span>
                    {pack.used} / {pack.initial} used
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  )
}
